/* ============================================================
   Deploy-time config — read from `VITE_*` env vars, which Vite inlines into
   the bundle at build time (the Expo app does the same with `EXPO_PUBLIC_*`;
   see app/src/auth/config.ts). None of it is secret: a public app-client id
   and public URLs. `desktop/.env` is gitignored because the values are
   deployment-specific, and desktop/deploy.sh writes it from Terraform outputs.

   The SHAPES and helpers come from @habishine/client-core; this module is only
   the Vite way of reading the values.
   ============================================================ */
import {
  type ClientCoreConfig,
  type CognitoConfig,
  parseSocialProviders,
  type SocialProvider,
} from '@habishine/client-core';

function required(v: string | undefined, name: string): string {
  if (v === undefined || v === '') {
    throw new Error(
      `Missing config: ${name} — set it in desktop/.env (see desktop/.env.example), then restart the dev server / rebuild.`,
    );
  }
  return v;
}

export const API_URL: string = required(import.meta.env.VITE_API_URL, 'VITE_API_URL');

/** VAPID public key for Web Push. Empty when the deployment has web push off —
   registration then reports `unavailable` and Settings says so. */
export const VAPID_PUBLIC_KEY: string = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '';

export const cognito: CognitoConfig = {
  region: required(import.meta.env.VITE_COGNITO_REGION, 'VITE_COGNITO_REGION'),
  userPoolId: required(import.meta.env.VITE_COGNITO_USER_POOL_ID, 'VITE_COGNITO_USER_POOL_ID'),
  clientId: required(import.meta.env.VITE_COGNITO_CLIENT_ID, 'VITE_COGNITO_CLIENT_ID'),
  domain: required(import.meta.env.VITE_COGNITO_DOMAIN, 'VITE_COGNITO_DOMAIN').replace(/\/+$/, ''),
  // see CognitoConfig.scopes in client-core for why `aws.cognito.signin.user.admin` is here
  scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
};

/** Which social buttons the sign-in page shows — Apple first, by the rule in
   parseSocialProviders. */
export const socialProviders: SocialProvider[] = parseSocialProviders(
  import.meta.env.VITE_SOCIAL_PROVIDERS,
);

/** Where "Get the app" points. The App Store link once the listing is live;
   until then the marketing site, which carries the download buttons. */
export const APP_STORE_URL: string = import.meta.env.VITE_APP_STORE_URL || 'https://habishine.com';

/** RevenueCat Web Billing public SDK key — `rcb_…` for production, `rcb_sb_…`
   for the sandbox (test cards, no money moves). Empty = web billing off: the
   paywall then says "subscribe in the iOS app", as it did before web billing
   existed. Public by design, like the app's `revenueCatKey`: it can start a
   checkout for the signed-in user and read that user's own entitlement, and
   nothing else. deploy.sh writes the production key here from
   desktop/infra's `revenuecat_web_key`, and the sandbox key to
   .env.development.local, which Vite reads only in `npm run dev`. */
export const REVENUECAT_WEB_KEY: string = import.meta.env.VITE_REVENUECAT_WEB_KEY ?? '';

export const clientConfig: ClientCoreConfig = {
  apiUrl: API_URL,
  cognito,
  // No native build to name, so no build floor applies (the server is lenient
  // on anything it can't read) — but say who we are, so one can be added later.
  clientHeaders: { 'X-App-Platform': 'web' },
};
